/**
 * ClickPay v2 design-system primitives.
 *
 * Small, token-driven building blocks shared app-wide. Everything styles
 * exclusively through the semantic tokens in src/index.css (bg-surface,
 * border-edge, text-ink-*, accent-*, status colors) so each primitive is
 * automatically correct in both light and dark themes.
 *
 * Usage rules: docs/DESIGN_SYSTEM.md.
 */
import { type ComponentProps, type ReactNode } from 'react'
import { Info } from 'lucide-react'
import { cn } from '../lib/cn'

/* ── Button ──────────────────────────────────────────────────────────────── */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'soft'
export type ButtonSize = 'xs' | 'sm' | 'md'

const BUTTON_VARIANT: Record<ButtonVariant, string> = {
  primary:
    'bg-accent text-accent-fg hover:bg-accent-hover border border-transparent shadow-card',
  secondary:
    'bg-surface text-ink border border-edge-strong hover:bg-surface-2 hover:border-edge-strong shadow-card',
  soft:
    'bg-accent-soft text-accent-soft-fg border border-transparent hover:bg-accent-soft-2',
  ghost:
    'bg-transparent text-ink-secondary border border-transparent hover:bg-surface-3 hover:text-ink',
  danger:
    'bg-danger text-white border border-transparent hover:opacity-90 shadow-card',
}

const BUTTON_SIZE: Record<ButtonSize, string> = {
  xs: 'h-6 px-2 text-xs gap-1 rounded-md',
  sm: 'h-7 px-2.5 text-[13px] gap-1.5 rounded-md',
  md: 'h-[34px] px-3.5 text-sm gap-2 rounded-lg',
}

export function Button({
  variant = 'secondary', size = 'md', className, ...props
}: ComponentProps<'button'> & { variant?: ButtonVariant; size?: ButtonSize }) {
  return (
    <button
      className={cn(
        'inline-flex items-center justify-center font-medium select-none',
        'transition-all duration-150 active:scale-[0.98]',
        'disabled:opacity-50 disabled:pointer-events-none',
        BUTTON_VARIANT[variant], BUTTON_SIZE[size], className,
      )}
      {...props}
    />
  )
}

export function IconButton({
  className, 'aria-label': ariaLabel, ...props
}: ComponentProps<'button'>) {
  return (
    <button
      aria-label={ariaLabel}
      className={cn(
        'inline-grid place-items-center h-7 w-7 rounded-md text-ink-muted',
        'hover:text-ink hover:bg-surface-3 transition-colors duration-150',
        'disabled:opacity-50 disabled:pointer-events-none',
        className,
      )}
      {...props}
    />
  )
}

/* ── Inputs ──────────────────────────────────────────────────────────────── */

export const inputClass =
  'h-[34px] px-3 text-sm rounded-lg bg-surface text-ink placeholder:text-ink-faint ' +
  'border border-edge-strong transition-[border-color,box-shadow] duration-150 ' +
  'focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 ' +
  'disabled:opacity-50 disabled:bg-surface-2'

export function Input({ className, ...props }: ComponentProps<'input'>) {
  return <input className={cn(inputClass, className)} {...props} />
}

export function Select({ className, children, ...props }: ComponentProps<'select'>) {
  return (
    <select className={cn(inputClass, 'pr-8 appearance-auto', className)} {...props}>
      {children}
    </select>
  )
}

/* ── Card / panel ────────────────────────────────────────────────────────── */

export function Card({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn('bg-surface rounded-xl border border-edge shadow-card', className)}
      {...props}
    />
  )
}

/** Standard card header strip: micro-label title on the left, actions right. */
export function CardHeader({
  title, icon, actions, className,
}: { title: ReactNode; icon?: ReactNode; actions?: ReactNode; className?: string }) {
  return (
    <div className={cn(
      'px-4 py-3 border-b border-edge-subtle flex items-center justify-between gap-2',
      className,
    )}>
      <div className="flex items-center gap-2 min-w-0">
        {icon && <span className="text-ink-faint shrink-0">{icon}</span>}
        <MicroLabel className="truncate">{title}</MicroLabel>
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  )
}

/** 11px tracked uppercase label — the system's section/eyebrow text style. */
export function MicroLabel({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-muted',
        className,
      )}
      {...props}
    />
  )
}

/* ── Badge / chip ────────────────────────────────────────────────────────── */

export type BadgeTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info'

const BADGE_TONE: Record<BadgeTone, string> = {
  neutral: 'bg-surface-3 text-ink-secondary border-edge',
  accent:  'bg-accent-soft text-accent-soft-fg border-accent-edge',
  success: 'bg-success-soft text-success-soft-fg border-success-edge',
  warning: 'bg-warning-soft text-warning-soft-fg border-warning-edge',
  danger:  'bg-danger-soft text-danger-soft-fg border-danger-edge',
  info:    'bg-info-soft text-info-soft-fg border-info-edge',
}

export function Badge({
  tone = 'neutral', bordered = false, className, ...props
}: ComponentProps<'span'> & { tone?: BadgeTone; bordered?: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs font-medium',
        'border', bordered ? '' : 'border-transparent',
        BADGE_TONE[tone], className,
      )}
      {...props}
    />
  )
}

/* ── Row-cap banner ──────────────────────────────────────────────────────── */

/**
 * B5-fe — several PPC list pages (Targets/Keywords/Placements/Campaigns/
 * SearchTerms/AdGroups) fetch with `limit: 50000` and no pagination; when a
 * grain genuinely exceeds the cap (keywords already does — 65,420 rows on
 * one account) the tail silently drops with no signal. Render this when
 * `count === cap` so the operator knows the table isn't the full picture.
 */
export function RowCapBanner({ count, cap = 50000 }: { count: number; cap?: number }) {
  if (count < cap) return null
  return (
    <div className="px-3 py-1.5 text-[12px] text-warning-soft-fg bg-warning-soft border-b border-edge-subtle">
      Showing the first {cap.toLocaleString('en-US')} rows — this grain has more than that and the rest are not
      loaded. Narrow the date range or filters to see everything.
    </div>
  )
}

/* ── Tabs ────────────────────────────────────────────────────────────────── */

export type TabItem<K extends string = string> = {
  key: K
  label: ReactNode
  /** A plain count, or a string like "100+" when the caller knows more rows
   *  exist beyond what's shown (e.g. an overfetch proved a cap was hit). */
  count?: number | string
}

/** Underline-style tab strip (Linear/Stripe idiom). Controlled. */
export function Tabs<K extends string>({
  items, active, onChange, className,
}: { items: TabItem<K>[]; active: K; onChange: (key: K) => void; className?: string }) {
  return (
    <div className={cn('flex items-center gap-1 border-b border-edge', className)} role="tablist">
      {items.map(t => {
        const isActive = t.key === active
        return (
          <button
            key={t.key}
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(t.key)}
            className={cn(
              'relative px-3 py-2 text-[13px] font-medium transition-colors duration-150 -mb-px',
              'border-b-2',
              isActive
                ? 'text-ink border-accent'
                : 'text-ink-muted border-transparent hover:text-ink hover:border-edge-strong',
            )}
          >
            <span className="inline-flex items-center gap-1.5">
              {t.label}
              {t.count !== undefined && (
                <span className={cn(
                  'px-1.5 py-0.5 rounded-full text-[10px] font-semibold tabular-nums leading-none',
                  isActive ? 'bg-accent-soft text-accent-soft-fg' : 'bg-surface-3 text-ink-muted',
                )}>
                  {t.count}
                </span>
              )}
            </span>
          </button>
        )
      })}
    </div>
  )
}

/* ── Tooltip ─────────────────────────────────────────────────────────────── */

/**
 * Lightweight CSS tooltip for short hints. Wraps its trigger; shows the
 * label above on hover/focus. For rich content use a popover instead.
 */
export function Tooltip({
  label, children, className,
}: { label: ReactNode; children: ReactNode; className?: string }) {
  return (
    <span className={cn('relative inline-flex group/tip', className)} tabIndex={-1}>
      {children}
      <span
        role="tooltip"
        className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-50
                   whitespace-nowrap rounded-md bg-overlay border border-edge shadow-pop
                   px-2 py-1 text-xs text-ink
                   opacity-0 translate-y-0.5 scale-95
                   transition-all duration-150 ease-out
                   group-hover/tip:opacity-100 group-hover/tip:translate-y-0 group-hover/tip:scale-100
                   group-focus-within/tip:opacity-100 group-focus-within/tip:translate-y-0 group-focus-within/tip:scale-100"
      >
        {label}
      </span>
    </span>
  )
}

/* ── Field help ──────────────────────────────────────────────────────────── */

/**
 * B12 — a small `?`/info glyph that shows explanatory help text on
 * hover/focus. Owner ask (2026-06-23 feedback): controls like the tree
 * editor / goal presets / value-type picker have no inline explanation of
 * what they do — this is the one shared primitive every page should reach
 * for instead of a bespoke tooltip. Prefer sourcing `text` from a
 * server-side description (e.g. bbpro_settings.description) when the field
 * has one; otherwise pass a short, plain-English string. Renders nothing
 * when `text` is empty so a page can pass an optional/loading description
 * without a conditional at every call site.
 */
export function FieldHelp({
  text,
  wide,
  className,
}: {
  text?: ReactNode
  wide?: boolean
  className?: string
}) {
  if (!text) return null
  return (
    <span
      className={cn('relative inline-flex group/fieldhelp align-middle', className)}
      tabIndex={0}
    >
      <Info size={12} className="text-ink-faint hover:text-ink-muted cursor-help" aria-hidden />
      <span className="sr-only">Help: {text}</span>
      <span
        role="tooltip"
        className={cn(
          'pointer-events-none absolute bottom-full left-1/2 z-50 mb-1.5 -translate-x-1/2',
          wide ? 'w-80' : 'w-56',
          '-translate-y-0.5 scale-95 whitespace-normal rounded-md border border-edge bg-overlay',
          'px-2 py-1.5 text-xs leading-snug text-ink opacity-0 shadow-pop',
          'transition-all duration-150 ease-out',
          'group-hover/fieldhelp:translate-y-0 group-hover/fieldhelp:scale-100 group-hover/fieldhelp:opacity-100',
          'group-focus-within/fieldhelp:translate-y-0 group-focus-within/fieldhelp:scale-100 group-focus-within/fieldhelp:opacity-100',
        )}
      >
        {text}
      </span>
    </span>
  )
}

/** Alias — some call sites read more naturally as "a tag with help" than
 *  "field help attached to a form control". Same component either name. */
export const HelpTag = FieldHelp

/* ── Skeleton / loading ──────────────────────────────────────────────────── */

export function Skeleton({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      aria-hidden
      className={cn('animate-pulse rounded-md bg-surface-3', className)}
      {...props}
    />
  )
}

/** Card-shaped skeleton with a label line + value line (KPI placeholder). */
export function SkeletonCard({ className }: { className?: string }) {
  return (
    <Card className={cn('p-5 space-y-3', className)}>
      <Skeleton className="h-3 w-24" />
      <Skeleton className="h-7 w-32" />
      <Skeleton className="h-3 w-40" />
    </Card>
  )
}
